import React, { useMemo, useState, useEffect, useCallback } from 'react';
import * as R from 'ramda';
import { css, useTheme } from '@emotion/react';
import {
	Avatar,
	Box,
	Button,
	Divider,
	IconButton,
	List,
	ListItem,
	ListItemText,
	Paper,
	Typography
} from '@mui/material';
import Delete from '@mui/icons-material/Delete';
import Settings from '@mui/icons-material/Settings';
import Warning from '@mui/icons-material/Warning';
import CreditCard from '@mui/icons-material/CreditCard';
import { useQueryClient } from '@cd/react-query';
import { capitalize } from '@cd/utils';
import { Elements } from '@stripe/react-stripe-js';
import { CenterProgress } from '../component/CenterProgress';
import { useMutationFetch, useQueryFetch } from './useQueryFetch';
import ConfirmButtonGroupCD from './ConfirmButtonGroupCD';
import {
	motion,
	AnimatePresence,
	usePresence,
	useAnimate,
	stagger
} from 'framer-motion';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import PaymentMethodSubmitForm from './PaymentMethodSubmitForm';
import { useStore } from './store';

const wrapStyle = css({
	padding: '16px',
	display: 'flex',
	flexDirection: 'column',
	minHeight: 320
});

const CardListContent = ({ brand, exp_month, exp_year, last4 }) => {
	return (
		<Box sx={{ display: 'flex', alignItems: 'center' }}>
			<Box sx={{ mr: 2 }}>
				<img src={`/api/image/get/${brand}`} alt="logo" />
			</Box>
			<ListItemText
				primary={`${capitalize(brand)} xxxx-${last4}`}
				secondary={`Expires: ${exp_month}/${exp_year}`}
			/>
		</Box>
	);
};

const deleteVariants = {
	open: { opacity: 1, transition: { duration: 0.4 } },
	close: { opacity: 0, transition: { duration: 0.4 } }
};

const DeleteMethodIconButton = ({ setConfirm }) => {
	const [isPresent, safeToRemove] = usePresence();
	return (
        <motion.div
			initial="close"
			animate={isPresent ? 'open' : 'close'}
			//onAnimationComplete={}
			onAnimationComplete={def => def === 'close' && safeToRemove()}
			variants={deleteVariants}>
            <IconButton
                color="primary"
                onClick={e => {
					setConfirm(true);
				}}
                size="large">
				<Delete />
			</IconButton>
        </motion.div>
    );
};

const DeletePaymentMethod = ({ id }) => {
	const queryClient = useQueryClient();
	const { dispatch } = useStore();
	const { mutate, isLoading, data } = useMutationFetch(
		'/payment/method/delete',
		{
			onSuccess: () => {
				dispatch({
					type: 'post success',
					message: 'Payment method deleted!'
				});
				queryClient.invalidateQueries('appPaymentMethods');
			}
		}
	);
	const [confirm, setConfirm] = useState(false);
	console.log('data', data);

	return (
		<Box sx={{ display: 'flex', alignItems: 'center' }}>
			<AnimatePresence>
				{confirm && (
					<ConfirmButtonGroupCD
						key="confirmDeletePayment"
						message="Are you sure?"
						onCancel={e => setConfirm(false)}
						onOk={e => {
							setConfirm(false);
							mutate({ paymentMethodID: id });
						}}
					/>
				)}
			</AnimatePresence>
			<AnimatePresence>
				{!confirm && !isLoading && (
					<DeleteMethodIconButton
						key="iconButton1"
						setConfirm={setConfirm}
					/>
				)}
			</AnimatePresence>
			<AnimatePresence>{isLoading && <CenterProgress />}</AnimatePresence>
		</Box>
	);
};

const CustomAvatar = ({ children, sx }) => {
	const { palette } = useTheme();
	return (
		<Avatar
			sx={{
				mr: 2,
				flex: '0 0 auto',
				...sx,
				backgroundColor: palette.primary.main
			}}>
			{children}
		</Avatar>
	);
};
function PaymentMethodList({ paymentMethods, invokedFromServiceForm }) {
	const [scope, animate] = useAnimate();

	useEffect(() => {
		// This "li" selector will only select children
		// of the element that receives `scope`.
		animate('li', { opacity: [0, 1] }, { delay: stagger(0.2) });
		animate('img', { scale: [0, 1] }, { type: 'spring' });
	}, []);
	return (
		<List ref={scope}>
			{paymentMethods.map(({ id, type, ...rest }) => (
				<ListItem
					sx={{ opacity: 0 }}
					secondaryAction={<DeletePaymentMethod id={id} />}>
					{type === 'card' ? (
						<CardListContent {...rest[type]} />
					) : (
						<ListItemText primary={type} />
					)}
				</ListItem>
			))}
			{(R.isNil(paymentMethods) || R.isEmpty(paymentMethods)) && (
				<>
					<ListItem sx={{ opacity: 0 }}>
						<CustomAvatar>
							<Warning />
						</CustomAvatar>
						<ListItemText
							primary="No saved payment methods."
							secondary="You must add a payment method first to authorize recurring payments."
						/>
						<img src="" alt="empty" style={{ display: 'none' }} />
					</ListItem>
					<ListItem sx={{ opacity: 0 }}>
						<CustomAvatar>
							<CreditCard />
						</CustomAvatar>
						<ListItemText
							primary="CREDIT CARD DISCLAIMER"
							secondary="Your security is important to us. We do not store your credit card information. Online payments are passed via a secure socket layer to a payment processor where your information is tokenized (whereby a random number is generated to represent your payment). The payment processor is PCI compliant which ensures that your information is being handled in accordance with industry security standards."
						/>
						<img src="" alt="empty" style={{ display: 'none' }} />
					</ListItem>
					{invokedFromServiceForm && (
						<ListItem sx={{ opacity: 0 }}>
							<CustomAvatar>
								<Settings />
							</CustomAvatar>
							<ListItemText
								primary="Payment Methods Settings"
								secondary={
									<div>
										You can view payment methods at:{' '}
										<Link
											to="/account/personal/paymentmethods"
											css={css({
												color: 'orange',
												textDecoration: 'none',
												cursor: 'pointer'
											})}>
											settings &#8594; payment methods
										</Link>{' '}
									</div>
								}
							/>
						</ListItem>
					)}
				</>
			)}
		</List>
	);
}

const PaymentMethods = ({ invokedFromServiceForm, userWrapStyle }) => {
	const [setupData, setSetupData] = useState(null);
	const { mutate, reset } = useMutationFetch('/payment/method/setup', {
		onSuccess: setupData => {
			setSetupData(setupData);
		}
	});

	const { isFetching, data: paymentMethods = [] } = useQueryFetch(
		'appPaymentMethods',
		'/payment/method/list'
	);

	const theme = useTheme();

	const isDark = theme.palette.mode === 'dark';

	const options = useMemo(() => {
		return setupData
			? {
					clientSecret: setupData.clientSecret,
					appearance: {
						theme: isDark ? 'night' : 'stripe',
						variables: {
							focusBoxShadow:
								'0px 2px 4px rgba(0, 0, 0, 0.5), 0px 1px 6px rgba(0, 0, 0, 0.25)',
							colorPrimary: theme.palette.primary.main,
							colorBackground: theme.palette.background.paper
						}
					}
			  }
			: {};
	}, [setupData]);

	const queryClient = useQueryClient();
	const mainButtonHandler = useCallback(
		() => {
			if (!setupData) {
				mutate({});
			} else {
				reset();
				setSetupData(null);
			}
		},
		null,
		[setupData]
	);

	const stripeData = queryClient.getQueryData('stripeKeyPub');
	if (isFetching) {
		return <CenterProgress />;
	}

	return (
		<Paper css={[wrapStyle, userWrapStyle]}>
			<Typography variant="h5" component="div">
				PAYMENT METHODS
			</Typography>
			<Divider />
			<Typography variant="caption" component="div">
				You can add and delete payment methods associated with this
				account
			</Typography>
			<Button
				sx={{ alignSelf: 'flex-end', mb: 2 }}
				onClick={mainButtonHandler}
				variant="outlined">
				{!setupData ? 'SET UP A NEW PAYMENT METHOD' : 'CANCEL'}
			</Button>
			{setupData && (
				<Elements stripe={stripeData} options={options}>
					<PaymentMethodSubmitForm
						onDone={e => setSetupData(null)}
						invokedFromServiceForm={invokedFromServiceForm}
					/>
				</Elements>
			)}
			{!setupData && (
				<PaymentMethodList
					paymentMethods={paymentMethods}
					invokedFromServiceForm={invokedFromServiceForm}
				/>
			)}
		</Paper>
	);
};
export default PaymentMethods;
